import {
  PartnerDealCommitteeEntryResponse,
  PartnerDealCommitteeRoundResponse,
  PartnerDealCommitteeVote,
  PartnerDealCommitteeVoterResponse,
  PartnerDealCommitteeViewResponse,
} from '@interfaces/partners/responses/partner-deal-committee.response';
import { PartnerDealStage } from '@interfaces/partners/responses/partner-deal.response';
import type { ProximoPasso } from '@app/features/dashboard/commercial/pages/deal-detail/deal-presenter';

/**
 * Como a tela conta o comitê de crédito do deal (card 3789): selos dos votantes, etiquetas da
 * discussão, o cartão de fase e o efeito de um voto. Funções puras, sem injeção, testadas em
 * `deal-committee-presenter.spec.ts`.
 *
 * ⚠️ **Nada aqui decide quem pode o quê.** `canVote`, `canComment`, `canReply` e `canSubmit` vêm
 * calculados do servidor; a maioria (`majority`), as contagens e se a rodada está segura pela
 * contestação (`isHeldByContest`) também. O presenter só transforma isso em texto. O único cálculo
 * local é o do aviso da modal de voto ("com o seu voto o deal é aprovado"), que soma o voto que
 * ainda não foi enviado às contagens do servidor: é uma previsão para quem vai confirmar, e quem
 * decide de verdade continua sendo o servidor.
 */

/** Tom de um selo, que vira a classe `committee-tag--{tone}`. */
export type CommitteeTagTone = 'success' | 'danger' | 'warning' | 'info' | 'neutral' | 'navy';

/** Selo de texto curto com o tom que o pinta. */
export interface CommitteeTag {
  label: string;
  tone: CommitteeTagTone;
}

/** Linha de apoio de um selo, com a data (ISO) que o template formata pelo `vaultDateTime`. */
export interface CommitteeDatedText {
  text: string;
  date: string | null;
}

/** Cartão de fase do comitê: o que o cartão escuro da lateral diz. */
export interface CommitteePhase {
  eyebrow: string;
  title: string;
  text: string;
  /** Pede atenção (contestação segurando a decisão, prazo vencido). */
  waiting: boolean;
}

/** Voto final: Aprovar e Negar não mudam depois de enviados. */
export function isFinalVote(vote: PartnerDealCommitteeVote | null | undefined): boolean {
  return vote === 'Approve' || vote === 'Deny';
}

/** A rodada está em votação. */
export function isRoundOpen(round: PartnerDealCommitteeRoundResponse | null | undefined): boolean {
  return round?.status === 'Open';
}

/** "#0004": o número da entrada, com quatro dígitos, como no protótipo. */
export function formatEntryNumber(value: number): string {
  return `#${String(value).padStart(4, '0')}`;
}

/** "Quórum: 3 sócios · 2 votos iguais decidem". */
export function describeQuorum(round: PartnerDealCommitteeRoundResponse): string {
  const count = round.voters.length;
  return `Quórum: ${count} ${count === 1 ? 'sócio' : 'sócios'} · ${round.majority} votos iguais decidem`;
}

/** Selo do resultado de uma rodada encerrada; `null` enquanto ela está em votação. */
export function roundResultTag(round: PartnerDealCommitteeRoundResponse): CommitteeTag | null {
  switch (round.status) {
    case 'Approved':
      return { label: 'Aprovado', tone: 'success' };
    case 'Denied':
      return { label: 'Negado', tone: 'danger' };
    case 'Cancelled':
      return { label: 'Cancelado', tone: 'neutral' };
    default:
      return null;
  }
}

/** Selo de um votante no cartão dele. */
export function voterBadge(
  round: PartnerDealCommitteeRoundResponse,
  voter: PartnerDealCommitteeVoterResponse
): CommitteeTag {
  if (voter.vote === 'Approve') return { label: 'Aprovou', tone: 'success' };
  if (voter.vote === 'Deny') return { label: 'Negou', tone: 'danger' };
  if (!isRoundOpen(round)) return { label: 'Não votou', tone: 'neutral' };
  if (voter.vote === 'Contest') {
    return voter.hasAnsweredContest
      ? { label: 'Contestação respondida', tone: 'info' }
      : { label: 'Contestou', tone: 'warning' };
  }
  if (round.isOverdue) return { label: 'Prazo vencido', tone: 'danger' };
  return { label: 'Aguardando voto', tone: 'neutral' };
}

/** A linha de baixo do cartão do votante: quando votou, ou por que não há voto. */
export function voterSubtitle(
  round: PartnerDealCommitteeRoundResponse,
  voter: PartnerDealCommitteeVoterResponse
): CommitteeDatedText {
  if (isFinalVote(voter.vote)) return { text: 'em', date: voter.votedAt };
  if (round.status === 'Cancelled')
    return { text: 'o comitê foi cancelado antes do voto', date: null };
  if (!isRoundOpen(round)) return { text: 'o comitê decidiu antes do voto', date: null };
  if (voter.vote === 'Contest') return { text: 'contestou em', date: voter.votedAt };
  // O selo "Aguardando voto" já diz que não há voto: a linha de baixo fica vazia.
  return { text: '', date: null };
}

/** Quem escreveu a entrada. As do sistema são assinadas pelo Cognito. */
export function entryAuthor(entry: PartnerDealCommitteeEntryResponse): string {
  if (entry.authorRole === 'System' || entry.kind === 'System') return 'Cognito';
  return entry.authorName?.trim() || 'Sem autor identificado';
}

/** Papel de quem escreveu: "Sócio" ou "Comercial". Vazio nas entradas do sistema. */
export function entryRoleLabel(entry: PartnerDealCommitteeEntryResponse): string {
  switch (entry.authorRole) {
    case 'Voter':
      return 'Sócio';
    case 'Submitter':
      return 'Comercial';
    default:
      return '';
  }
}

/**
 * Etiqueta de uma entrada da discussão.
 *
 * @param entry Entrada.
 * @param isCurrentRound A entrada é da rodada exibida como atual. Contestação aberta de rodada
 *   anterior não existe mais (vira `Closed`), mas a etiqueta não pode dizer "em aberto" para ela.
 */
export function entryTag(
  entry: PartnerDealCommitteeEntryResponse,
  isCurrentRound: boolean
): CommitteeTag {
  switch (entry.kind) {
    case 'Approve':
      return { label: 'Votou: aprovar', tone: 'success' };
    case 'Deny':
      return { label: 'Votou: negar', tone: 'danger' };
    case 'Contest':
      switch (entry.contestStatus) {
        case 'Answered':
          return { label: 'Contestação respondida', tone: 'info' };
        case 'Withdrawn':
          return { label: 'Contestação encerrada pelo voto', tone: 'neutral' };
        case 'Closed':
          return { label: 'Contestação encerrada com o comitê', tone: 'neutral' };
        default:
          return isCurrentRound
            ? { label: 'Contestação em aberto', tone: 'warning' }
            : { label: 'Contestação de rodada anterior', tone: 'neutral' };
      }
    case 'Reply':
      return { label: 'Resposta do comercial', tone: 'info' };
    case 'Comment':
      return { label: 'Comentário', tone: 'neutral' };
    default:
      return { label: 'Registro automático', tone: 'navy' };
  }
}

/** "em resposta a #0004", nas respostas do comercial. */
export function entryReference(entry: PartnerDealCommitteeEntryResponse): string {
  return entry.replyToNumber ? `em resposta a ${formatEntryNumber(entry.replyToNumber)}` : '';
}

/** Contestações em aberto da rodada, da mais antiga para a mais nova. */
export function openContests(
  round: PartnerDealCommitteeRoundResponse | null | undefined
): PartnerDealCommitteeEntryResponse[] {
  if (!round) return [];
  return round.entries
    .filter(e => e.kind === 'Contest' && e.contestStatus === 'Open')
    .sort((a, b) => a.number - b.number);
}

/** O votante que é quem está vendo, se for votante da rodada. */
export function currentVoter(
  round: PartnerDealCommitteeRoundResponse | null | undefined
): PartnerDealCommitteeVoterResponse | null {
  return round?.voters.find(v => v.isMe) ?? null;
}

/**
 * O que acontece com o voto que a pessoa vai confirmar, para a modal de Aprovar/Negar.
 *
 * Soma o voto ainda não enviado às contagens do servidor. A contestação de quem vota sai da conta
 * (voto final retira as contestações abertas dele, R4); a dos outros continua segurando a decisão.
 */
export function describeVoteEffect(
  round: PartnerDealCommitteeRoundResponse,
  vote: 'Approve' | 'Deny'
): string {
  const approvals = round.approvals + (vote === 'Approve' ? 1 : 0);
  const denials = round.denials + (vote === 'Deny' ? 1 : 0);
  // `isMine` vem do servidor (autor = quem está vendo): a contestação própria sai da conta.
  const othersOpen = openContests(round).filter(e => !e.isMine).length;

  if (approvals >= round.majority || denials >= round.majority) {
    if (othersOpen > 0) {
      return 'A maioria se forma, mas o comitê só decide depois que a contestação em aberto for respondida.';
    }
    return vote === 'Approve'
      ? 'Com o seu voto o deal é aprovado e vai para Cotação.'
      : 'Com o seu voto o deal é negado e vai para Rejeitados.';
  }
  return 'O deal continua esperando os outros votos.';
}

/** Texto completo da modal de confirmação do voto. */
export function describeVoteConfirmation(
  round: PartnerDealCommitteeRoundResponse,
  vote: 'Approve' | 'Deny'
): { title: string; text: string; confirmLabel: string } {
  const effect = describeVoteEffect(round, vote);
  if (vote === 'Approve') {
    return {
      title: 'Aprovar o deal?',
      text: `Seu voto vai como Aprovar e não pode ser mudado depois. ${effect}`,
      confirmLabel: 'Confirmar aprovação',
    };
  }
  return {
    title: 'Negar o deal?',
    text: `Seu voto vai como Negar, com a justificativa abaixo, e não pode ser mudado depois. A justificativa aparece para o parceiro se o deal for negado. ${effect}`,
    confirmLabel: 'Confirmar negação',
  };
}

/** Mensagem de sucesso depois de um voto, a partir do comitê como o servidor devolveu. */
export function describeVoteResult(
  vote: PartnerDealCommitteeVote,
  after: PartnerDealCommitteeRoundResponse | null | undefined
): string {
  if (after?.status === 'Approved')
    return 'Comitê decidido: deal aprovado. A etapa avançou para Cotação.';
  if (after?.status === 'Denied') {
    return 'Comitê decidido: deal negado. O deal foi para Rejeitados e o parceiro foi avisado.';
  }
  if (vote === 'Contest') {
    return 'Contestação enviada ao comercial, no sino e por e-mail. O comitê não decide até ela ser respondida.';
  }
  const base = `Voto registrado: ${vote === 'Approve' ? 'Aprovar' : 'Negar'}.`;
  return after?.isHeldByContest
    ? `${base} A maioria se formou; o comitê decide quando a contestação for respondida.`
    : base;
}

/** Mensagem de sucesso depois de responder a uma contestação. */
export function describeReplyResult(
  after: PartnerDealCommitteeRoundResponse | null | undefined,
  contestAuthor: string | null
): string {
  if (after?.status === 'Approved')
    return 'Comitê decidido: deal aprovado. A etapa avançou para Cotação.';
  if (after?.status === 'Denied') {
    return 'Comitê decidido: deal negado. O deal foi para Rejeitados e o parceiro foi avisado.';
  }
  return contestAuthor
    ? `Resposta enviada. ${contestAuthor} recebe aviso para votar.`
    : 'Resposta enviada. Quem contestou recebe aviso para votar.';
}

/**
 * Validação local do texto antes de chamar o servidor, com as mesmas regras dele (R4): Negar e
 * Contestar exigem texto (mínimo 5 caracteres); Aprovar não. Devolve a mensagem ou `null`.
 */
export function validateVoteText(vote: PartnerDealCommitteeVote, text: string): string | null {
  const trimmed = (text ?? '').trim();
  if (vote === 'Deny' && trimmed.length < 5) {
    return 'Escreva a justificativa antes de negar: ela fica registrada, é obrigatória para Negar e aparece para o parceiro se o deal for negado.';
  }
  if (vote === 'Contest' && trimmed.length < 5) {
    return 'Escreva o que precisa ser respondido: a contestação vai para o comercial.';
  }
  return null;
}

/** Nomes em lista: "A", "A e B", "A, B e C". */
export function joinNames(names: string[]): string {
  const clean = names.map(n => n.trim()).filter(Boolean);
  if (clean.length <= 1) return clean[0] ?? '';
  return `${clean.slice(0, -1).join(', ')} e ${clean[clean.length - 1]}`;
}

/** Quem ainda não deu voto final na rodada. */
export function pendingVoterNames(round: PartnerDealCommitteeRoundResponse): string[] {
  return round.voters.filter(v => !isFinalVote(v.vote)).map(v => v.name);
}

/**
 * O cartão escuro da lateral, quando o deal está no comitê (rodada em votação) ou pronto para ir.
 *
 * @param view Comitê como o servidor devolveu.
 */
export function describeCommitteePhase(view: PartnerDealCommitteeViewResponse): CommitteePhase {
  const round = view.current;

  if (!round || round.status === 'Cancelled') {
    const nextRound = (round?.round ?? view.previousRounds.length) + 1;
    return {
      eyebrow: 'Comitê de crédito',
      title: nextRound > 1 ? `Pronto para a ${nextRound}ª rodada` : 'Pronto para o comitê',
      text: 'Quando a análise econômico-financeira estiver pronta, o comercial envia o deal ao comitê; os sócios votantes recebem o pedido de voto no sino e por e-mail.',
      waiting: false,
    };
  }

  if (round.status === 'Approved') {
    return {
      eyebrow: 'Decisão do comitê',
      title: 'Deal aprovado',
      text: 'A etapa avançou sozinha para Cotação: o comercial já pode levar o deal aos bancos. O parceiro continua vendo Análise até o contrato ser enviado.',
      waiting: false,
    };
  }

  if (round.status === 'Denied') {
    return {
      eyebrow: 'Decisão do comitê',
      title: 'Deal negado',
      text: 'O deal foi para Rejeitados com o motivo "Negado pelo comitê de crédito". O parceiro vê o deal como Rejeitado, com as justificativas de quem negou. Reabrir o deal o leva de volta para Análise, para uma nova rodada.',
      waiting: false,
    };
  }

  const contests = openContests(round);
  let text: string;
  if (round.isHeldByContest && contests.length) {
    const author = contests[0].authorName?.trim() || 'um sócio';
    text = `A maioria já se formou, mas a contestação de ${author} segura a decisão. Assim que o comercial responder, o comitê decide.`;
  } else if (round.openContestCount > 0) {
    text =
      round.openContestCount === 1
        ? 'Há 1 contestação em aberto com o comercial. Enquanto ela estiver aberta, o comitê não decide.'
        : `Há ${round.openContestCount} contestações em aberto com o comercial. Enquanto houver contestação aberta, o comitê não decide.`;
  } else {
    text = `Aguardando os votos. ${round.majority} aprovações aprovam; ${round.majority} negações negam.`;
  }

  return {
    eyebrow: round.isOverdue ? 'Prazo vencido' : 'Em andamento',
    title: 'Em comitê',
    text,
    waiting: round.isOverdue || round.isHeldByContest,
  };
}

/**
 * O cartão "próximo passo" da tela do deal nas etapas Análise e Comitê (card 3789). Devolve `null`
 * quando o comitê não tem o que dizer, e aí vale a régua normal do `resolverProximoPasso`.
 *
 * - Análise, time interno: o próximo passo é **Enviar ao comitê** (com `SUBMIT`), não "Avançar
 *   etapa", que levaria o deal ao Comitê sem rodada nenhuma.
 * - Comitê com rodada em votação: a fase do comitê, sem botão (o backend recusa avançar com rodada
 *   aberta; o deal vai para Cotação sozinho quando aprovado).
 * - Comitê sem rodada em votação (nunca enviado, movido à mão, rodada cancelada) ou comitê ainda não
 *   carregado: `null`, e vale a régua normal ("Avançar etapa" continua disponível). O envio aparece
 *   no próprio painel e como ação secundária do cartão ({@link shouldOfferCommitteeSubmit}).
 *
 * @param stage Etapa atual do deal.
 * @param internalView Quem vê é do time interno.
 * @param canSubmitPermission Quem vê tem `PARTNERDEALCOMMITTEE_SUBMIT`.
 * @param view Comitê carregado pelo painel (nulo enquanto o painel não carregou).
 */
export function resolveCommitteeNextStep(
  stage: PartnerDealStage | null | undefined,
  internalView: boolean,
  canSubmitPermission: boolean,
  view: PartnerDealCommitteeViewResponse | null
): ProximoPasso | null {
  if (!internalView) return null;

  if (stage === 'Analise') {
    if (canSubmitPermission) {
      return {
        tom: 'ok',
        eyebrow: 'Próximo passo',
        titulo: 'Enviar ao comitê de crédito',
        texto:
          'Quando a análise econômico-financeira estiver pronta, envie o deal ao comitê: os sócios votantes recebem o pedido de voto no sino e por e-mail.',
        acao: 'submitToCommittee',
      };
    }
    return {
      tom: 'aguardando',
      eyebrow: 'Próximo passo',
      titulo: 'Comitê de crédito',
      texto:
        'Quem envia o deal ao comitê é o comercial do deal ou quem estiver na lista de quem envia, em Parametrização > Comitê de crédito do deal. Peça a essa pessoa para enviar quando a análise estiver pronta.',
      acao: null,
    };
  }

  if (stage !== 'Comite') return null;

  if (!view) return null;

  // Comitê sem votação aberta (deal movido à mão, ou rodada cancelada): o caminho para Cotação é
  // o comitê aprovar, então o cartão pede o envio em vez de oferecer "Avançar etapa" (E2E 30/10).
  if (!isRoundOpen(view.current)) {
    if (view.canSubmit) {
      return {
        tom: 'ok',
        eyebrow: 'Próximo passo',
        titulo: 'Enviar ao comitê de crédito',
        texto:
          'O deal está em Comitê sem votação aberta. Envie ao comitê: os sócios votantes recebem o pedido de voto no sino e por e-mail, e a aprovação leva o deal para Cotação.',
        acao: 'submitToCommittee',
      };
    }
    return {
      tom: 'aguardando',
      eyebrow: 'Próximo passo',
      titulo: 'Comitê de crédito',
      texto:
        'O deal está em Comitê sem votação aberta. Quem envia ao comitê é o comercial do deal ou quem estiver na lista de quem envia, em Parametrização > Comitê de crédito do deal.',
      acao: null,
    };
  }

  const phase = describeCommitteePhase(view);
  return {
    tom: phase.waiting ? 'aguardando' : 'ok',
    eyebrow: phase.eyebrow,
    titulo: phase.title,
    texto: phase.text,
    acao: null,
  };
}

/**
 * Se o cartão "próximo passo" oferece também **Enviar ao comitê** (botão secundário, ao lado do
 * "Avançar etapa"): deal no Comitê sem rodada em votação (nunca enviado ou rodada cancelada) e o
 * servidor diz que quem vê pode enviar.
 *
 * @param stage Etapa atual do deal.
 * @param internalView Quem vê é do time interno.
 * @param view Comitê carregado pelo painel.
 */
export function shouldOfferCommitteeSubmit(
  stage: PartnerDealStage | null | undefined,
  internalView: boolean,
  view: PartnerDealCommitteeViewResponse | null
): boolean {
  return (
    internalView && stage === 'Comite' && !!view && !isRoundOpen(view.current) && view.canSubmit
  );
}

/**
 * A rejeição vigente do deal (a última da linha do tempo) veio do comitê de crédito? Reabrir esse
 * deal o devolve para Análise, para nova rodada, e não para a etapa em que parou (card 3789).
 */
export function isCommitteeDenial(
  entries: ReadonlyArray<{ eventType: string; rejectionReason?: string | null }> | null | undefined
): boolean {
  const last = (entries ?? [])
    .slice()
    .reverse()
    .find(e => e.eventType === 'Rejected');
  return last?.rejectionReason === 'NegadoPeloComite';
}
